import type { ResponseBody, SearchRequest } from "@elastic/search-ui-elasticsearch-connector";

export const USP_FACET_FIELDS = ["ItemLocalUSP.value.keyword", "ItemGeneralUSP.value.keyword"] as const;

type Aggregation = Record<string, unknown>;
type Bucket = { key: { value: string }; doc_count: number };

// Composite pagination returns every label with exact counts across shards.
// Preserve the connector's filter ancestors, including disjunctive filters.
export async function searchWithCompleteUspFacets(
  request: SearchRequest,
  next: (request: SearchRequest) => Promise<ResponseBody>,
): Promise<ResponseBody> {
  const targets: { path: string[]; aggregation: Aggregation }[] = [];
  const visit = (aggregations: Aggregation, path: string[] = []) => {
    for (const [name, raw] of Object.entries(aggregations)) {
      const aggregation = raw as Aggregation;
      const terms = aggregation.terms as { field?: string } | undefined;
      if (USP_FACET_FIELDS.some((field) => field === terms?.field)) {
        delete aggregation.terms;
        aggregation.composite = { size: 100, sources: [{ value: { terms: { field: terms!.field } } }] };
        targets.push({ path: [...path, name], aggregation });
      } else {
        const children = aggregation.aggs ?? aggregation.aggregations;
        if (children) visit(children as Aggregation, [...path, name]);
      }
    }
  };
  visit((request.aggregations ?? request.aggs ?? {}) as Aggregation);
  const response = await next(request);
  const atPath = (result: ResponseBody, path: string[]) => {
    let node = result.aggregations as Aggregation;
    for (const segment of path) node = node?.[segment] as Aggregation;
    if (!node || !Array.isArray(node.buckets)) throw new Error("USP facet values could not be loaded.");
    return node;
  };
  for (const { path, aggregation } of targets) {
    const original = atPath(response, path);
    let page = original;
    const buckets: Bucket[] = [];
    const cursors = new Set<string>();
    while (true) {
      buckets.push(...page.buckets as Bucket[]);
      if (!page.after_key || !(page.buckets as Bucket[]).length) break;
      const cursor = JSON.stringify(page.after_key);
      if (cursors.has(cursor)) throw new Error("USP facet pagination did not advance.");
      cursors.add(cursor);
      // Copy only this facet and its ancestors; avoid reloading hits and other facets.
      let tree: Aggregation = { [path.at(-1)!]: { ...aggregation, composite: { ...aggregation.composite as Aggregation, after: page.after_key } } };
      const ancestors: Aggregation[] = [];
      let source = (request.aggregations ?? request.aggs) as Aggregation;
      for (const segment of path.slice(0, -1)) {
        const ancestor = source[segment] as Aggregation;
        ancestors.push(ancestor);
        source = (ancestor.aggs ?? ancestor.aggregations) as Aggregation;
      }
      for (let i = ancestors.length - 1; i >= 0; i--) {
        const ancestor = { ...ancestors[i] };
        delete ancestor.aggs;
        delete ancestor.aggregations;
        tree = { [path[i]]: { ...ancestor, aggs: tree } };
      }
      const followup: SearchRequest = { ...request, size: 0, from: 0, aggregations: tree as SearchRequest["aggregations"] };
      delete followup.aggs;
      page = atPath(await next(followup), path);
    }
    original.buckets = buckets.map(({ key, doc_count }) => ({ key: key.value, doc_count }))
      .sort((a, b) => b.doc_count - a.doc_count || a.key.localeCompare(b.key));
    delete original.after_key;
  }
  return response;
}
